
import { EquipmentFitments } from "@/api";
import {
  EquipmentFitmentItem,
  EquipmentFitmentLine
} from "@/api/equipmentFitments";
import TitleButtons from "@/components/common/TitleButtons.vue";
import { Component, Prop, Vue, Watch } from "vue-property-decorator";

/** VAT is charged on a fitment as it is on a quote. */
const VAT_RATE = 0.15;

/**
 * What a line comes to: the unit price with its discount off, rounded to
 * the cent, times the quantity. The same sum the server does.
 */
const lineTotal = (line: EquipmentFitmentLine): number => {
  const unit =
    Math.round(
      Number(line.price) * (1 - Number(line.discount || 0) / 100) * 100
    ) / 100;
  return Math.round(Number(line.qty) * unit * 100) / 100;
};

/**
 * The items on a fitment: the compliance list for the kind of vehicle, each
 * ticked on or off with a quantity and a price, plus any line added by hand.
 * The lines are the model; the compliance list only decides which names are
 * offered up front.
 */
@Component({ components: { TitleButtons } })
export default class EquipmentFitmentItemsTable extends Vue {
  @Prop({ default: () => [] })
  value!: EquipmentFitmentLine[];

  @Prop({ default: null })
  vehicleTypeId!: number | null;

  @Prop({ default: false })
  readOnly!: boolean;

  @Prop({ default: false })
  showTotals!: boolean;

  complianceItems: EquipmentFitmentItem[] = [];
  allItemNames: string[] = [];
  newLineName: string | null = null;
  loading = false;

  async created(): Promise<void> {
    // Every item, so a part can be added by hand even when it is not on the
    // compliance list for this vehicle.
    try {
      this.allItemNames = (await EquipmentFitments.items()).map(
        item => item.name
      );
    } catch (e) {
      this.allItemNames = [];
    }
  }

  /**
   * The compliance list follows the kind of vehicle. Lines already on the
   * fitment are kept whatever the list says, so nothing saved disappears.
   */
  @Watch("vehicleTypeId", { immediate: true })
  async onVehicleTypeChange(id: number | null): Promise<void> {
    this.complianceItems = [];
    if (!id) return;

    this.loading = true;
    try {
      const items = await EquipmentFitments.items(id);
      // Guard against a slower reply for a type that is no longer selected.
      if (this.vehicleTypeId !== id) return;
      this.complianceItems = items;
    } catch (e) {
      await this.$error(e);
    } finally {
      this.loading = false;
    }
  }

  /** The compliance list first, then whatever was added by hand. */
  get rows(): Array<{ name: string; itemId: number | null; price: number }> {
    const compliance = this.complianceItems.map(item => ({
      name: item.name,
      itemId: item.id || null,
      price: Number(item.price)
    }));
    const known = compliance.map(row => row.name);
    const manual = this.value
      .filter(line => !known.includes(line.name))
      .map(line => ({
        name: line.name,
        itemId: line.itemId,
        price: line.price
      }));

    return [...compliance, ...manual];
  }

  get subtotal(): number {
    return (
      Math.round(
        this.value.reduce((total, line) => total + lineTotal(line), 0) * 100
      ) / 100
    );
  }

  get vat(): number {
    return Math.round(this.subtotal * VAT_RATE * 100) / 100;
  }

  isChecked(name: string): boolean {
    return this.value.some(line => line.name === name);
  }

  lineFor(name: string): EquipmentFitmentLine {
    return (
      this.value.find(line => line.name === name) || {
        itemId: null,
        name,
        qty: 1,
        price: 0
      }
    );
  }

  lineTotal(name: string): number {
    return lineTotal(this.lineFor(name));
  }

  toggle(
    row: { name: string; itemId: number | null; price: number },
    checked: boolean
  ): void {
    if (!checked) {
      this.emit(this.value.filter(line => line.name !== row.name));
      return;
    }
    if (this.isChecked(row.name)) return;
    this.emit([
      ...this.value,
      {
        itemId: row.itemId,
        name: row.name,
        qty: 1,
        price: row.price,
        discount: 0
      }
    ]);
  }

  setQuantity(name: string, value: string): void {
    this.patch(name, { qty: Math.max(1, Number(value) || 1) });
  }

  setDiscount(name: string, value: string): void {
    this.patch(name, {
      discount: Math.min(100, Math.max(0, Number(value) || 0))
    });
  }

  addManualLine(): void {
    const name = (this.newLineName || "").trim();
    if (!name) return;

    if (!this.isChecked(name)) {
      const known = this.complianceItems.find(item => item.name === name);
      this.emit([
        ...this.value,
        {
          itemId: known && known.id ? known.id : null,
          name,
          qty: 1,
          price: known ? Number(known.price) : 0,
          discount: 0
        }
      ]);
    }

    this.newLineName = null;
  }

  formatMoney(value: number): string {
    return `R ${Number(value || 0).toFixed(2)}`;
  }

  private patch(name: string, change: Partial<EquipmentFitmentLine>): void {
    this.emit(
      this.value.map(line =>
        line.name === name ? { ...line, ...change } : line
      )
    );
  }

  private emit(lines: EquipmentFitmentLine[]): void {
    // Keep the compliance order, then the hand-added lines in the order given.
    const order = this.rows.map(row => row.name);
    const sorted = [...lines].sort(
      (a, b) => order.indexOf(a.name) - order.indexOf(b.name)
    );
    this.$emit("input", sorted);
  }
}
